import { Injectable } from '@angular/core';
import { Api } from './api';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private userNameSubject = new BehaviorSubject<string>('');
  private userDataSubject = new BehaviorSubject<any>(null);
  public userName$ = this.userNameSubject.asObservable();
  public userData$ = this.userDataSubject.asObservable();
  private dataLoadedSubject = new BehaviorSubject<boolean>(false);
  public dataLoaded$ = this.dataLoadedSubject.asObservable();

  constructor(private api: Api) {}

  setToken(token: string): void {
    localStorage.setItem('crmtoken', token);
  }

  getToken(): string | null {
    const token = localStorage.getItem('crmtoken');
    return token;
  }

  decodeToken(): any {
    const token = this.getToken();
    if (!token) {
      return null;
    }

    try {
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload));
      return decoded;
    } catch (e) {
      return null;
    }
  }

loadUserData(): Promise<any> {
  console.log('🔄 SessionService: loadUserData iniciado');
  return new Promise((resolve, reject) => {
    const decoded = this.decodeToken();
    if (!decoded || !decoded.userId) {
      console.error('❌ Token inválido o sin userId');
      this.dataLoadedSubject.next(false);
      reject('Token inválido o sin userId');
      return;
    }

    console.log('📞 SessionService: Llamando a API getUserPermiso');
    this.api.getUserPermiso({ usuario_id: decoded.userId }).subscribe({
      next: (res: any) => {
        console.log('✅ SessionService: Respuesta de API recibida', res);
        if (res?.success && res?.usuario) {
          const userData = res.usuario;
          const fullName = `${userData.nombre} ${userData.apellido}`;
          
          this.userNameSubject.next(fullName);

          const completeUserData = {
            ...userData,
            permisos: res.permisos
          };

          this.userDataSubject.next(completeUserData);
          localStorage.setItem('userData', JSON.stringify(completeUserData));
          localStorage.setItem('userName', fullName);
          this.dataLoadedSubject.next(true);
          
          console.log('✅ SessionService: Datos de usuario guardados');
          resolve(completeUserData);
        } else {
          console.error('❌ SessionService: Respuesta sin datos válidos');
          this.dataLoadedSubject.next(false);
          reject('Respuesta sin datos válidos');
        }
      },
      error: (err) => {
        console.error('❌ SessionService: Error al obtener datos:', err);
        this.dataLoadedSubject.next(false);
        reject(err);
      },
    });
  });
}

  fetchUserData(): void {
    const decoded = this.decodeToken();
    if (!decoded || !decoded.userId) {
      console.warn('No se pudo obtener el userId del token:', decoded);
      return;
    }

    this.api.getUserPermiso({ usuario_id: decoded.userId }).subscribe({
      next: (res: any) => {
        if (res?.success && res?.usuario) {
          const userData = res.usuario;
          const fullName = `${userData.nombre} ${userData.apellido}`;
          
          this.userNameSubject.next(fullName);

          const completeUserData = {
            ...userData,
            permisos: res.permisos
          };

          this.userDataSubject.next(completeUserData);
          localStorage.setItem('userData', JSON.stringify(completeUserData));
          localStorage.setItem('userName', fullName);
        } else {
          console.warn('Respuesta sin datos válidos:', res);
        }
      },
      error: (err) => {
        console.error('Error al obtener datos del usuario:', err);
        const storedName = localStorage.getItem('userName');
        if (storedName) {
          this.userNameSubject.next(storedName);
        }
      },
    });
  }

  getUserName(): string {
    const currentName = this.userNameSubject.value;
    if (currentName) {
      return currentName;
    }

    const storedName = localStorage.getItem('userName');
    if (storedName) {
      return storedName;
    }

    return '';
  }

  getUserData(): any {
    const currentData = this.userDataSubject.value;
    if (currentData) {
      return currentData;
    }

    const storedData = localStorage.getItem('userData');
    if (storedData) {
      return JSON.parse(storedData);
    }

    return null;
  }

  clearUserData(): void {
    this.userNameSubject.next('');
    this.userDataSubject.next(null);
    localStorage.removeItem('userName');
    localStorage.removeItem('crmtoken');
    localStorage.removeItem('userData');
  }

  getUserId(): string | null {
    const decoded = this.decodeToken();
    return decoded?.userId || null;
  }

  getUserEmail(): string {
    const userData = this.getUserData();
    return userData?.email || '';
  }

  getUserFullName(): string {
    const userData = this.getUserData();
    if (userData?.nombre && userData?.apellido) {
      return `${userData.nombre} ${userData.apellido}`;
    }
    return this.getUserName();
  }

  getUserRut(): string {
    const userData = this.getUserData();
    return userData?.rut || '';
  }

  getUserRole(): string {
    const userData = this.getUserData();
    return userData?.rolId || '';
  }

  getUserCreationDate(): Date | null {
    const userData = this.getUserData();
    return userData?.fecha_creacion ? new Date(userData.fecha_creacion) : null;
  }

  updateUserData(newData: any): void {
    const currentUserData = this.getUserData() || {};
    const updatedUserData = { ...currentUserData, ...newData };

    this.userDataSubject.next(updatedUserData);
    localStorage.setItem('userData', JSON.stringify(updatedUserData));

    if (updatedUserData.nombre && updatedUserData.apellido) {
      const fullName = `${updatedUserData.nombre} ${updatedUserData.apellido}`;
      this.userNameSubject.next(fullName);
      localStorage.setItem('userName', fullName);
    }
  }

  isUserDataComplete(): boolean {
    const userData = this.getUserData();

    if (!userData) {
      return false;
    }

    const hasBasicInfo = userData.nombre && userData.apellido && userData.email;

    return hasBasicInfo;
  }
}